import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

type InsiderBuyingInfoParams = {
  symbol: string;
  page: number;
  pageSize: number;
  sortBy?: string;
  lang: Lang;
};

const buyingInfoSchema = z
  .object({
    buyerName: z.string().nullable(),
    buyerTitle: z.string().nullable(),
    filingAt: z.string().nullable(),
    stockCategory: z.string().nullable(),
    tradeCash: z.number().nullable(),
    tradeAt: z.string().nullable(),
    tradeNum: z.number().nullable(),
    tradeNumAfter: z.number().nullable(),
  })
  .partial();

const insiderBuyingInfoSchema = z
  .object({
    data: z.array(buyingInfoSchema).nullish(),
    page: z.number().nullish(),
    pageSize: z.number().nullish(),
    total: z.number().nullish(),
    updatedAt: z.string().nullish(),
  })
  .partial();

export type InsiderBuyingInfo = z.infer<typeof insiderBuyingInfoSchema>;
export type BuyingInfo = z.infer<typeof buyingInfoSchema>;

export const getInsiderBuyingInfo = async ({
  symbol,
  page,
  pageSize,
  sortBy,
  lang,
}: InsiderBuyingInfoParams): Promise<InsiderBuyingInfo> => {
  const endpoint = `/insider-buying-info/${symbol}/${page}/${pageSize}`;
  const res = await fetch(
    `${NIKE_URL}${endpoint}${sortBy ? `?sortBy=${sortBy}` : ""}`,
    {
      method: "GET",
      headers: { "Accept-Language": lang },
    }
  );
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return insiderBuyingInfoSchema.parse(await res.json());
};

export const useInsiderBuyingInfo = ({
  symbol,
  page = 1,
  pageSize = 10,
  sortBy,
}: {
  symbol: string;
  page: number;
  pageSize: number;
  sortBy?: string;
}) => {
  const { lang } = useLang();
  return useQuery(
    ["insider-buying-info", symbol, page, pageSize, sortBy, lang],
    () => getInsiderBuyingInfo({ symbol, page, pageSize, sortBy, lang }),
    {
      enabled: !!symbol && !!page && !!pageSize,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
};
